import React, { useState, useEffect, useMemo, useRef, useId } from 'react';
import { useNavigate, useParams, useLocation, Navigate } from 'react-router-dom';
import {
  Box, TableBody, TableContainer, TableHead, TableRow, CircularProgress, Alert, Button,
  Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, IconButton,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import styled, { css, keyframes } from 'styled-components';
import recruiterTheme, {
  FONT_BODY, COLORS, TYPE, RADIUS, humanizeStatus,
} from '../../styles/recruiterTheme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchProctoringCandidate, fetchProctoringSnapshot, describeApiError, isNotFound } from './proctoringApi';
import {
  ProctoringGlobalStyle, PageWrapper, HeaderBar, BackBtn, PageTitle, PageSubtitle,
  Card, CardTitle, CardSub, Muted, EmptyNote, Pill, StatusPill, ReasonPill,
  StyledTable, HeadCell, BodyRow, BodyCell, ReasonBars, EpisodeTimeline,
  normalizeSession, groupByAttempt, roundLabel, questionLabel, calibrationText, reasonInfo,
  fmtClock, fmtDateTime, fmtTime, fmtSeconds, toMs, plural, useNow, ROUND_ORDER,
  readStaffUser, goBackOrDashboard,
} from './proctoringShared';
import ProctoringSummaryCard from './ProctoringSummaryCard';

const FONT = FONT_BODY;

const fmtDate = (ms) => (Number.isFinite(ms)
  ? new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : 'Unknown');

const durationOf = (e) => {
  const d = Number(e.duration_s);
  if (Number.isFinite(d)) return d;
  const a = toMs(e.start_t);
  const b = toMs(e.end_t);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, (b - a) / 1000) : 0;
};

// ---- Local chrome ----------------------------------------------------------

const HeaderGrid = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
`;

const Name = styled.div`
  font-family: ${FONT_BODY};
  font-weight: 800;
  font-size: ${TYPE.section}px;
  color: ${COLORS.primaryDeep};
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const SubLine = styled.div`
  font-size: ${TYPE.small}px;
  color: ${COLORS.muted};
  font-weight: 500;
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

const FlagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: ${(p) => p.$gradient || 'linear-gradient(135deg,#6750A4,#4F378B)'};
  border-radius: ${RADIUS.lg}px;
  padding: 16px 24px;
  color: #fff;
  min-width: 144px;
  box-shadow: 0 4px 16px ${(p) => p.$shadow || 'rgba(103,80,164,0.26)'};
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.page}px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  margin-bottom: 8px;
`;

const StatLbl = styled.div`
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  opacity: 0.9;
  text-transform: uppercase;
`;

const SectionTitle = styled.div`
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.section}px;
  color: ${COLORS.primaryDeep};
  margin: 32px 0 16px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RoundGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const MetaLine = styled.div`
  font-size: ${TYPE.caption}px;
  color: ${COLORS.ink};
  font-weight: 500;
  padding: 2px 0;
`;

// ---- Snapshots -------------------------------------------------------------
// One camera image per episode. The endpoint needs the Bearer header, so each
// image is fetched as a blob and shown through an object URL. The page owns one
// store: it keeps one object URL per snapshot id, runs at most SNAPSHOT_PARALLEL
// requests at a time, and revokes every URL in clear().

const THUMB_PX = 56;
const SNAPSHOT_PARALLEL = 4;
const SNAPSHOT_ZOOM = 2; // images are at most 320 px on the long side; never shown beyond 2x
const SNAPSHOT_ID_RE = /^[0-9a-fA-F]{24}$/;
const NO_FRAME_REASONS = ['camera_lost', 'stream_gap'];

const SNAPSHOT_IDLE = { status: 'idle', url: null };
const SNAPSHOT_LOADING = { status: 'loading', url: null };

const snapshotIdOf = (e) => {
  const raw = e ? e.snapshot_id : null;
  const value = raw && typeof raw === 'object' && raw.$oid ? raw.$oid : raw;
  return typeof value === 'string' && SNAPSHOT_ID_RE.test(value) ? value : null;
};

const noImageReason = (e) => {
  if (NO_FRAME_REASONS.includes(e.reason)) return 'The camera was not delivering frames during this episode, so there is no image.';
  if (e.snapshot_id === undefined) return 'This episode was recorded before snapshots were kept.';
  return 'No image was saved for this episode.';
};

const loadFailedReason = (entry) => (entry.status === 'missing'
  ? 'The image for this episode was not found.'
  : 'The image could not be loaded.');

const episodeText = (e) => `${reasonInfo(e.reason).label}, ${roundLabel(e.round)}, ${fmtTime(e.startMs)}`;

const createSnapshotStore = () => {
  const entries = new Map();     // snapshot id -> { status, url }
  const listeners = new Map();   // snapshot id -> Set of callbacks from mounted components
  const controllers = new Set(); // AbortControllers of the requests in flight
  const queue = [];
  let active = 0;
  let generation = 0;            // bumped by clear(); answers from an older generation are dropped

  const emit = (id) => {
    const set = listeners.get(id);
    if (!set) return;
    const entry = entries.get(id) || SNAPSHOT_IDLE;
    Array.from(set).forEach((fn) => fn(entry));
  };

  // Starts the next queued requests, up to SNAPSHOT_PARALLEL in flight.
  const pump = () => {
    while (active < SNAPSHOT_PARALLEL && queue.length > 0) {
      const id = queue.shift();
      // No listener left means every row that asked has gone. A later subscribe queues it again.
      if (listeners.has(id)) load(id);
      else entries.delete(id);
    }
  };

  const load = (id) => {
    const startedIn = generation;
    const controller = new AbortController();
    controllers.add(controller);
    active += 1;
    fetchProctoringSnapshot(id, { signal: controller.signal })
      .then((blob) => {
        // An object URL is only made for the current generation, so clear() never misses one.
        if (startedIn !== generation) return;
        entries.set(id, { status: 'ready', url: URL.createObjectURL(blob) });
      })
      .catch((err) => {
        if (startedIn !== generation) return;
        entries.set(id, { status: isNotFound(err) ? 'missing' : 'failed', url: null });
      })
      .then(() => {
        controllers.delete(controller);
        if (startedIn !== generation) return;
        active -= 1;
        pump();
        emit(id);
      });
  };

  // Calls fn with the current entry now and on every change. Returns the unsubscribe.
  const subscribe = (id, fn) => {
    let set = listeners.get(id);
    if (!set) {
      set = new Set();
      listeners.set(id, set);
    }
    set.add(fn);
    if (!entries.has(id)) {
      entries.set(id, SNAPSHOT_LOADING);
      queue.push(id);
      pump();
    }
    fn(entries.get(id) || SNAPSHOT_LOADING);
    return () => {
      set.delete(fn);
      if (set.size === 0 && listeners.get(id) === set) listeners.delete(id);
    };
  };

  // The bytes arrived but the browser could not decode them.
  const markBroken = (id) => {
    const entry = entries.get(id);
    if (!entry || entry.status !== 'ready') return;
    URL.revokeObjectURL(entry.url);
    entries.set(id, { status: 'failed', url: null });
    emit(id);
  };

  const clear = () => {
    generation += 1;
    controllers.forEach((controller) => controller.abort());
    controllers.clear();
    queue.length = 0;
    active = 0;
    entries.forEach((entry) => { if (entry.url) URL.revokeObjectURL(entry.url); });
    entries.clear();
    listeners.clear();
  };

  return { subscribe, markBroken, clear };
};

// Entry for one snapshot id. Nothing is requested until `wanted` is true.
// The entry is kept with the id it belongs to, so a component that moves to
// another id never shows the previous image, not even for one frame.
const useSnapshot = (store, snapshotId, wanted) => {
  const [seen, setSeen] = useState({ id: null, entry: SNAPSHOT_IDLE });
  useEffect(() => {
    if (!wanted || !snapshotId) return undefined;
    return store.subscribe(snapshotId, (entry) => setSeen({ id: snapshotId, entry }));
  }, [store, snapshotId, wanted]);
  return wanted && seen.id === snapshotId ? seen.entry : SNAPSHOT_IDLE;
};

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const SnapshotCell = styled(BodyCell)`
  && {
    width: ${THUMB_PX + 32}px;
    padding: 8px 16px !important;
  }
`;

// Fixed box, so a row keeps its height whichever state the image is in.
const ThumbBox = styled.div`
  width: ${THUMB_PX}px;
  height: ${THUMB_PX}px;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.sm}px;
  background: ${COLORS.background};
  color: ${COLORS.muted};
  font-size: ${TYPE.overline}px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  overflow: hidden;
  ${(p) => p.$pulse && css`
    animation: ${pulse} 1.4s ease-in-out infinite;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  `}
`;

const ThumbButton = styled.button`
  width: ${THUMB_PX}px;
  height: ${THUMB_PX}px;
  flex: none;
  display: block;
  padding: 0;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.sm}px;
  background: ${COLORS.background};
  overflow: hidden;
  cursor: zoom-in;
  transition: box-shadow 0.16s;
  &:hover { box-shadow: 0 3px 12px rgba(79,55,139,0.24); }
  &:focus-visible { outline: 2px solid #6750A4; outline-offset: 2px; }
  img { display: block; width: 100%; height: 100%; object-fit: cover; }
`;

const SnapshotFigure = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: ${FONT};
`;

const SnapshotFrame = styled.div`
  min-width: min(320px, 100%);
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border: 1px solid ${COLORS.border};
  border-radius: ${RADIUS.md}px;
  background: ${COLORS.background};
  img { display: block; max-width: 100%; height: auto; border-radius: 8px; }
`;

const SnapshotCaption = styled.figcaption`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: ${TYPE.small}px;
  font-weight: 500;
  color: ${COLORS.ink};
`;

const CaptionRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const SnapshotThumb = ({ store, episode, snapshotId, onOpen }) => {
  const boxRef = useRef(null);
  const [inView, setInView] = useState(false);
  const entry = useSnapshot(store, snapshotId, inView);

  // Ask for the image only once the row is near the viewport.
  useEffect(() => {
    if (!snapshotId || inView) return undefined;
    const node = boxRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      // No observer in this browser: ask straight away. The store still limits parallel requests.
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver((hits) => {
      if (hits.some((hit) => hit.isIntersecting)) setInView(true);
    }, { rootMargin: '160px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [snapshotId, inView]);

  if (!snapshotId) {
    const why = noImageReason(episode);
    return <ThumbBox role="img" aria-label={`No image. ${why}`} title={why}>No image</ThumbBox>;
  }
  if (entry.status === 'ready') {
    const what = episodeText(episode);
    return (
      <ThumbButton type="button" onClick={onOpen} title="Show this image larger" aria-label={`Show the camera image larger. ${what}`}>
        <img
          src={entry.url}
          alt={`Camera view when this episode was flagged. ${what}`}
          width={THUMB_PX}
          height={THUMB_PX}
          draggable={false}
          onError={() => store.markBroken(snapshotId)}
        />
      </ThumbButton>
    );
  }
  if (entry.status === 'failed' || entry.status === 'missing') {
    const why = loadFailedReason(entry);
    return <ThumbBox role="img" aria-label={`Image not loaded. ${why}`} title={why}>Not loaded</ThumbBox>;
  }
  return <ThumbBox ref={boxRef} $pulse role="img" aria-label="Loading the camera image" />;
};

const SnapshotDialog = ({ store, open, episode, onClose }) => {
  const titleId = useId();
  const snapshotId = episode ? snapshotIdOf(episode) : null;
  const entry = useSnapshot(store, snapshotId, Boolean(snapshotId));
  const [natural, setNatural] = useState(null);
  const fit = natural && natural.url === entry.url && natural.w > 0 ? natural : null;

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={titleId} maxWidth="md" PaperProps={{ sx: { borderRadius: `${RADIUS.lg}px` } }}>
      <DialogTitle id={titleId} sx={{ color: COLORS.primaryDeep, pr: 7 }}>
        Camera image
      </DialogTitle>
      <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: COLORS.muted }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ pt: 0 }}>
        {episode && (
          <SnapshotFigure>
            <SnapshotFrame>
              {entry.status === 'ready' && (
                <img
                  src={entry.url}
                  alt={`Camera view when this episode was flagged. ${episodeText(episode)}`}
                  style={fit ? { width: fit.w * SNAPSHOT_ZOOM } : undefined}
                  onLoad={(ev) => setNatural({ url: entry.url, w: ev.currentTarget.naturalWidth })}
                  onError={() => store.markBroken(snapshotId)}
                />
              )}
              {(entry.status === 'idle' || entry.status === 'loading') && <CircularProgress size={28} />}
              {(entry.status === 'failed' || entry.status === 'missing') && <Muted>{loadFailedReason(entry)}</Muted>}
            </SnapshotFrame>
            <SnapshotCaption>
              <CaptionRow>
                <ReasonPill reason={episode.reason} />
                <span>{roundLabel(episode.round)} {'·'} {questionLabel(episode.question_index)}</span>
              </CaptionRow>
              <div>
                Started {fmtTime(episode.startMs)}, {fmtDate(episode.startMs)} {'·'} Lasted {fmtSeconds(durationOf(episode))}
              </div>
              <Muted>Camera image from the moment this episode was flagged.</Muted>
            </SnapshotCaption>
          </SnapshotFigure>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---- Pieces ----------------------------------------------------------------

const RoundCard = ({ round, session }) => (
  <Card elevation={0}>
    <CardTitle>
      <span>{roundLabel(round)}</span>
      {session
        ? <StatusPill statusKey={session.statusKey} title={session.endedByLabel || undefined} />
        : <Pill $bg="#F1F5F9" $fg="#8B7DB5">Not started</Pill>}
    </CardTitle>
    {!session ? (
      <EmptyNote $compact>Not started. This card fills in when the candidate begins the round with the camera on.</EmptyNote>
    ) : (
      <>
        <MetaLine>Attempt {session.attempt} {'·'} Started {fmtDateTime(session.startedMs)}</MetaLine>
        <MetaLine>
          Monitored {fmtClock(session.monitoredS)} {'·'} Frames {session.frames === null ? 'pending' : session.frames} {'·'} Warnings {session.warnings}
        </MetaLine>
        {session.endedByLabel && (
          <MetaLine>
            <Muted>{session.endedByLabel}{Number.isFinite(session.endedMs) ? ` · ${fmtDateTime(session.endedMs)}` : ''}</Muted>
          </MetaLine>
        )}
        <Box sx={{ my: 2 }}>
          <ReasonBars session={session} />
        </Box>
        <MetaLine><Muted>{calibrationText(session)}</Muted></MetaLine>
      </>
    )}
  </Card>
);

const AttemptView = ({ group, now, snapshots }) => {
  // The episode stays set after closing so the dialog keeps its content while it fades out.
  const [viewer, setViewer] = useState({ open: false, episode: null });
  const byRound = {};
  group.sessions.forEach((s) => { byRound[s.round] = s; });
  const rounds = Array.from(new Set(ROUND_ORDER.concat(group.sessions.map((s) => s.round))));
  const episodes = group.sessions
    .flatMap((s) => s.episodes.map((e) => ({ ...e, round: s.round, startMs: toMs(e.start_t) })))
    .sort((a, b) => (Number.isFinite(a.startMs) ? a.startMs : 0) - (Number.isFinite(b.startMs) ? b.startMs : 0));

  return (
    <Stack>
      <RoundGrid>
        {rounds.map((r) => <RoundCard key={r} round={r} session={byRound[r]} />)}
      </RoundGrid>

      {group.sessions.map((s) => (
        <Card key={`${s.round}-${s.attempt}-timeline`} elevation={0}>
          <CardTitle>
            <span>{roundLabel(s.round)} timeline</span>
            <StatusPill statusKey={s.statusKey} title={s.endedByLabel || undefined} />
          </CardTitle>
          <CardSub>One row per reason. Blue is time with no episode open. Hover a segment for the question and duration.</CardSub>
          <EpisodeTimeline session={s} now={now} />
        </Card>
      ))}

      <Card elevation={0}>
        <CardTitle>
          <span>Episodes</span>
          <Muted>{plural(episodes.length, 'episode')}</Muted>
        </CardTitle>
        {episodes.length === 0 ? (
          <EmptyNote $compact>No episodes in this attempt.</EmptyNote>
        ) : (
          <>
            <CardSub>
              Each row carries the camera image from the moment it was flagged. Select an image to see it larger. Rows without one say why on hover.
            </CardSub>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <StyledTable size="small">
                <TableHead>
                  <TableRow>
                    <HeadCell>Snapshot</HeadCell>
                    <HeadCell>Round</HeadCell>
                    <HeadCell>Question</HeadCell>
                    <HeadCell>Reason</HeadCell>
                    <HeadCell>Start</HeadCell>
                    <HeadCell>Duration</HeadCell>
                    <HeadCell align="center">Reminders</HeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {episodes.map((e, i) => {
                    const snapshotId = snapshotIdOf(e);
                    return (
                      <BodyRow key={`${e.round}-${i}`}>
                        <SnapshotCell>
                          <SnapshotThumb
                            key={snapshotId || 'none'}
                            store={snapshots}
                            episode={e}
                            snapshotId={snapshotId}
                            onOpen={() => setViewer({ open: true, episode: e })}
                          />
                        </SnapshotCell>
                        <BodyCell>{roundLabel(e.round)}</BodyCell>
                        <BodyCell>{questionLabel(e.question_index)}</BodyCell>
                        <BodyCell><ReasonPill reason={e.reason} /></BodyCell>
                        <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box>{fmtTime(e.startMs)}</Box>
                          <Muted>{fmtDate(e.startMs)}</Muted>
                        </BodyCell>
                        <BodyCell>{fmtSeconds(durationOf(e))}</BodyCell>
                        <BodyCell align="center">{Number(e.reminders) || 0}</BodyCell>
                      </BodyRow>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </TableContainer>
          </>
        )}
      </Card>
      <SnapshotDialog
        store={snapshots}
        open={viewer.open}
        episode={viewer.episode}
        onClose={() => setViewer((v) => ({ ...v, open: false }))}
      />
    </Stack>
  );
};

// ---- Page ------------------------------------------------------------------

const ProctoringCandidateView = ({ user }) => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const now = useNow(30000);
  const [candidate, setCandidate] = useState(null);
  const [rawSessions, setRawSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [snapshots] = useState(createSnapshotStore);

  // Camera images are kept for the life of the page: aborted and revoked when it
  // unmounts or moves to another candidate.
  useEffect(() => () => snapshots.clear(), [snapshots, candidateId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchProctoringCandidate(candidateId)
      .then(({ candidate: c, sessions }) => {
        if (cancelled) return;
        setCandidate(c);
        setRawSessions(sessions);
      })
      .catch((err) => {
        if (cancelled) return;
        setCandidate(null);
        setRawSessions([]);
        if (isNotFound(err)) setNotFound(true);
        else setError(describeApiError(err, 'Could not load this candidate'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [candidateId, reloadKey]);

  const sessions = useMemo(() => rawSessions.map((row) => normalizeSession(row, now)), [rawSessions, now]);
  const groups = useMemo(() => groupByAttempt(sessions), [sessions]);
  const latest = groups.length ? groups[0] : null;
  const older = groups.slice(1);

  const violations = (candidate && candidate.proctoringViolations) || {};
  const proctoring = (candidate && candidate.proctoring) || {};
  const overrideOn = Boolean(proctoring.override);
  const needsReview = Boolean(proctoring.needs_human_review);

  const renderBody = () => {
    if (loading) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2} minHeight="50vh">
          <CircularProgress />
          <Muted>Loading camera proctoring…</Muted>
        </Box>
      );
    }
    if (notFound) {
      return (
        <Card elevation={0}>
          <EmptyNote>No candidate with this id.</EmptyNote>
        </Card>
      );
    }
    if (error) {
      return (
        <Alert
          severity="error"
          sx={{ borderRadius: `${RADIUS.md}px` }}
          action={<Button color="inherit" size="small" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}
        >
          {error}
        </Alert>
      );
    }
    if (!candidate) {
      return (
        <Card elevation={0}>
          <EmptyNote>No candidate with this id.</EmptyNote>
        </Card>
      );
    }
    return (
      <>
        <Card elevation={0}>
          <HeaderGrid>
            <Box>
              <Name>{candidate.candidatename || 'Unnamed candidate'}</Name>
              <SubLine>
                {[candidate.candidateEmail, candidate.jobRole].filter(Boolean).join(' · ') || 'No contact details on file'}
              </SubLine>
              <FlagRow>
                {candidate.overallStatus && <Pill $bg={COLORS.accentSoft} $fg={COLORS.primaryDark}>{humanizeStatus(candidate.overallStatus)}</Pill>}
                <Pill $bg={violations.autoSubmitted ? '#FDECEA' : '#F1F5F9'} $fg={violations.autoSubmitted ? '#B3261E' : '#475569'}>
                  Auto submitted: {violations.autoSubmitted ? 'Yes' : 'No'}
                </Pill>
                <Pill
                  $bg={overrideOn ? '#FFF8E1' : '#F1F5F9'}
                  $fg={overrideOn ? '#E65100' : '#475569'}
                  title={overrideOn ? 'Continued after a rejected calibration. Gaze verdicts need a human before they count.' : undefined}
                >
                  Calibration override: {overrideOn ? 'Yes' : 'No'}
                </Pill>
                {needsReview && <Pill $bg="#FFF8E1" $fg="#E65100">Needs human review</Pill>}
              </FlagRow>
            </Box>
            <StatsRow>
              <StatCard>
                <StatNum>{Number(violations.cameraCount) || 0}</StatNum>
                <StatLbl>Camera warnings</StatLbl>
              </StatCard>
              <StatCard $gradient="linear-gradient(135deg,#E91E8C,#C2185B)" $shadow="rgba(233,30,140,0.24)">
                <StatNum>{Number(violations.count) || 0}</StatNum>
                <StatLbl>Tab switches</StatLbl>
              </StatCard>
              <StatCard $gradient="linear-gradient(135deg,#1565C0,#0D47A1)" $shadow="rgba(21,101,192,0.24)">
                <StatNum>{groups.length}</StatNum>
                <StatLbl>Camera attempts</StatLbl>
              </StatCard>
            </StatsRow>
          </HeaderGrid>
        </Card>
        {latest && <ProctoringSummaryCard candidate={candidate} group={latest} olderAttempts={older.length} />}

        {!latest ? (
          <Card elevation={0} sx={{ mt: 3 }}>
            <EmptyNote>No camera sessions for this candidate yet. They appear when the candidate starts a round.</EmptyNote>
          </Card>
        ) : (
          <>
            <SectionTitle>Attempt {latest.attempt}{groups.length > 1 ? ' (latest)' : ''}</SectionTitle>
            <AttemptView group={latest} now={now} snapshots={snapshots} />
          </>
        )}

        {older.length > 0 && (
          <Box>
            <SectionTitle>Older attempts</SectionTitle>
            {older.map((g) => {
              const firstStart = g.sessions.reduce((acc, s) => (Number.isFinite(s.startedMs) && s.startedMs < acc ? s.startedMs : acc), Infinity);
              return (
                <Accordion
                  key={g.attempt}
                  disableGutters
                  elevation={0}
                  TransitionProps={{ unmountOnExit: true }}
                  sx={{
                    border: `1px solid ${COLORS.border}`, borderRadius: `${RADIUS.lg}px !important`, mb: 2, background: COLORS.surface,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3 }}>
                    <Box sx={{ fontWeight: 700, color: COLORS.primaryDeep, fontSize: TYPE.body }}>
                      Attempt {g.attempt}
                      <Muted>{` · ${plural(g.sessions.length, 'round')} · started ${Number.isFinite(firstStart) ? fmtDateTime(firstStart) : 'Unknown'}`}</Muted>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 3, pt: 2, background: COLORS.surfaceTint, borderTop: `1px solid ${COLORS.border}` }}>
                    <AttemptView group={g} now={now} snapshots={snapshots} />
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}
      </>
    );
  };

  return (
    <PageWrapper>
      <ProctoringGlobalStyle />
      <HeaderBar>
        <BackBtn onClick={() => goBackOrDashboard(navigate, user)} aria-label="Back" title="Back">
          <ArrowBackIcon />
        </BackBtn>
        <Box>
          <PageTitle>Camera proctoring</PageTitle>
          <PageSubtitle>Every episode is listed. Nothing here is a verdict.</PageSubtitle>
        </Box>
      </HeaderBar>
      {renderBody()}
    </PageWrapper>
  );
};

// Standalone staff route: no saved user means no session, so send them to sign in.
const ProctoringCandidatePage = () => {
  const location = useLocation();
  const user = readStaffUser(location.state);
  if (!user) return <Navigate to="/login" replace />;
  return (
    <ThemeProvider theme={recruiterTheme}>
      <ProctoringCandidateView user={user} />
    </ThemeProvider>
  );
};

export default ProctoringCandidatePage;
